<script setup>
import { ref, computed, watch, defineAsyncComponent } from 'vue';
import CarouselControls from '@/components/shared/CarouselControls.vue';
import ArchiveControls from '@/components/shared/ArchiveControls.vue';
import TTS from '@/components/shared/Tts.vue';

const VuePdfEmbed = defineAsyncComponent(() => import('vue-pdf-embed'));

const emit = defineEmits(['archive-add', 'archive-remove', 'close-preview']);

const props = defineProps({
  documents: {
    type: Array,
    default: () => [],
  },
  selectedItemIndex: {
    type: Number,
    default: 0,
  },
  allowArchiveRemove: {
    type: Boolean,
    default: false,
  },
});

const currentDocumentIndex = ref(props.selectedItemIndex);

// Keep in sync whenever a different preview is opened (component instance is reused).
watch(
  () => props.selectedItemIndex,
  (index) => {
    currentDocumentIndex.value = index;
  }
);

// Clamp so a shrinking list (e.g. after archive-remove) can't leave a stale, out-of-range index.
const currentDocument = computed(() => {
  const index = Math.min(
    currentDocumentIndex.value,
    props.documents.length - 1
  );

  return props.documents[Math.max(index, 0)];
});

function goNext() {
  const nextIndex = currentDocumentIndex.value + 1;

  currentDocumentIndex.value =
    nextIndex >= props.documents.length ? 0 : nextIndex;
}

function goPrevious() {
  const previousIndex = currentDocumentIndex.value - 1;

  currentDocumentIndex.value =
    previousIndex < 0 ? props.documents.length - 1 : previousIndex;
}

function archiveAddRemove(addOrRemove) {
  emit(`archive-${addOrRemove}`, {
    mediaType: 'documents',
    id: currentDocument.value.id,
  });
}
</script>

<template>
  <div v-if="currentDocument" class="flex-1">
    <div
      class="flex flex-col items-center overflow-hidden mb-12 mt-56"
      :style="{ height: '1200px' }"
    >
      <!-- Use source="/sample.pdf" for local development to avoid CORS errors -->
      <!-- :key forces a fresh viewer per document so scroll/page position doesn't carry over -->
      <VuePdfEmbed
        :key="currentDocument.id"
        :source="currentDocument.url"
        :width="800"
        class="overflow-scroll"
      />
    </div>
  </div>

  <TTS
    v-if="currentDocument"
    buttonPosition="justify-center"
    class="mb-12"
    :pdf="currentDocument.url"
  />

  <ArchiveControls
    :allowArchiveRemove="allowArchiveRemove"
    mediaType="documents"
    @archive-add="archiveAddRemove('add')"
    @archive-remove="archiveAddRemove('remove')"
  />

  <CarouselControls
    media-type="DOCUMENTS"
    :total-slides="documents.length"
    :current-slide="currentDocumentIndex + 1"
    :autoplay="false"
    @previous="goPrevious()"
    @next="goNext()"
    @close="$emit('close-preview')"
  />
</template>
